/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: TimezoneUtil.ts
 * Created by: chan.ying
 * Create Date: 2026-03-31 14:18
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

export interface TimezoneInfo {
    id: string;
    continent: string;
    region: string;
    offset: string;
}

export interface TimezoneContinent {
    continent: string;
    timezoneList: TimezoneInfo[];
}

export default class TimezoneUtil {
    private static _instance: TimezoneUtil;

    public static get Instance(): TimezoneUtil {
        if (!TimezoneUtil._instance) {
            TimezoneUtil._instance = new TimezoneUtil();
        }
        return TimezoneUtil._instance;
    }

    public static init(): void {
        if (!this._instance) {
            this._instance = new TimezoneUtil();
        }
        this._instance.initialize();
    }

    private constructor() {
    }

    private groupedTimezones: TimezoneContinent[] = [];
    private initialized: boolean = false;

    private initialize(): this {
        if (this.initialized) return this;

        try {
            const ids = (Intl as any).supportedValuesOf('timeZone') as string[];

            const groups = ids.reduce<Record<string, TimezoneContinent>>((acc, id) => {
                const parts = id.split('/');
                const continent = parts[0];
                const region = parts.length > 1
                    ? parts.slice(1).join('/').replace(/_/g, ' ')
                    : id;

                if (!acc[continent]) {
                    acc[continent] = { continent, timezoneList: [] };
                }

                acc[continent].timezoneList.push({
                    id,
                    continent,
                    region,
                    offset: this.formatOffset(id)
                });

                return acc;
            }, {});

            this.groupedTimezones = Object.values(groups).sort((a, b) =>
                a.continent.localeCompare(b.continent)
            );

            this.initialized = true;
        } catch (e) {
            console.error('TimezoneUtil initialization failure:', e);
        }

        return this;
    }

    getGroupedList(): TimezoneContinent[] {
        return this.groupedTimezones;
    }

    getFlatList(): TimezoneInfo[] {
        return this.groupedTimezones.flatMap(g => g.timezoneList);
    }

    getByContinent(continent: string): TimezoneInfo[] {
        return this.groupedTimezones.find(g => g.continent === continent)?.timezoneList ?? [];
    }

    getById(id: string): TimezoneInfo | null {
        return this.getFlatList().find(tz => tz.id === id) ?? null;
    }

    getCurrentTimezone(): TimezoneInfo | null {
        const id = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return this.getById(id);
    }

    private formatOffset(timeZone: string): string {
        try {
            const str = new Date().toLocaleString('en-US', { timeZone, timeZoneName: 'shortOffset' });
            const match = str.match(/GMT([+-]\d+(:?\d+)?)/);
            if (!match) return '+00:00';
            let offset = match[1];
            if (!offset.includes(':')) {
                offset = offset[0] + offset.slice(1).padStart(2, '0') + ':00';
            }
            return offset;
        } catch {
            return '+00:00';
        }
    }
}